import React from "react";
import RfxResponseComp from "newui/features/Rfx/Submission/components/RfxSubmissionStatusPill/RfxResponseComp";
import { Button } from "newui/components/Button";
import dayjs from "newui/utils/dayjs";
import { Flex } from "@chakra-ui/core";
import { timezoneChecker } from "utils/helpers";
import { Avatar } from "components/RFQ/Avatar";
import { AsyncImage } from "loadable-image";
import { DEFAULT_IMAGE_BANNER, RFX_STATUS, RFX_TABS } from "utils/constants";
import { SubmissionDeadline } from "../SubmissionDeadline/SubmissionDeadline";
import { Rfx } from "newui/features/Rfx/types/rfx.types";
import { GuestInviteType } from "types/auth.types";
import { GUEST_INVITE_TYPE } from "constants/guest";
import { isRfxApproved } from "helpers/rfxHelper";
import RFQStatusComp from "../RFQStatusComp";
import UnreadMessage from "newui/components/UnreadMessage";
import { RFxCardData, useSupplierRfxNavigator } from "newui/features/Rfx/hooks/useSupplierRfxNavigator";

interface RFxCardProps {
	data: RFxCardData;
}

const RfqCard: React.FC<RFxCardProps> = ({ data }) => {
	const rfxId = data?.rfqId ?? data?.rfq?.id;
	const navigateToRfx = useSupplierRfxNavigator();

	const renderButtonRFQView = (
		submissionStatus: string | undefined,
		rfxStatus: string | undefined,
		invite_type: GuestInviteType | undefined,
		rfx: Rfx
	): string => {
		const statusName =
			typeof submissionStatus === "string"
				? submissionStatus.toUpperCase()
				: submissionStatus;

		if (invite_type && invite_type === GUEST_INVITE_TYPE.RFX_PRE_APPROVER) {
			if (!statusName || statusName === RFX_STATUS.DRAFT) {
				return isRfxApproved(rfx)
					? "View RFx"
					: "View RFx to Approve Submission";
			}
		}

		if (
			statusName === RFX_STATUS.IN_PROGRESS &&
			rfxStatus?.toUpperCase() !== RFX_STATUS.EVALUATION
		) {
			return "Edit RFx";
		} else if (
			statusName === RFX_STATUS.INVITED &&
			rfxStatus?.toUpperCase() !== RFX_STATUS.EVALUATION
		) {
			return "Preview RFx Invitation";
		} else {
			return "View RFx";
		}
	};

	const handleRfxCardClick = () => {
		if (!rfxId) {
			return;
		}

		navigateToRfx(data);
	};

	const defaultBackgroundImage = (
		<img
			src={DEFAULT_IMAGE_BANNER}
			alt="banner-card"
			style={{ filter: "blur(10px)" }}
		/>
	);

	const submissionDeadlineDateTime = data?.rfq?.submissionDeadline
		? new Date(data?.rfq?.submissionDeadline)
		: null;
	const invite_type = data?.invite_type;

	const TypeSafeAvatar = (
		// @ts-ignore
		<Avatar
			avatar={data?.rfq?.project?.logoPath}
			size="lg"
		/>
	);

	const navigateToComms = () => {
		navigateToRfx(data, RFX_TABS.COMMS);
	}

	return (
		<div
			className="card-shadow mb-3 mr-5 mt-3 w-full overflow-hidden rounded-md bg-white"
			style={{ minWidth: "320px" }}
			data-testid={`rfq-card-${data?.id}`}
		>
			<div className="h-[6px] w-full bg-royal-blue2"></div>
			<div className="pb-5 pl-5 pr-5 pt-3">
				<div
					className="relative"
					style={{ height: "60px" }}
				>
					<div className="absolute w-full">
						<AsyncImage
							src={`${data?.rfq?.project?.backgroundImagePath || ""}`}
							alt="banner-card"
							className="h-[60px]"
							error={defaultBackgroundImage}
							loader={defaultBackgroundImage}
						/>
					</div>
					<div className="px-2 pt-2">{TypeSafeAvatar}</div>
				</div>
				<div className="grid grid-cols-[1fr_20px] gap-2">
					<div>
						<h3 className="mt-3 line-clamp-1 overflow-hidden font-roboto text-[20px] font-semibold capitalize text-primary-900">
							{data?.rfq?.name || "Project"}
						</h3>
						<div className="mb-4 font-roboto text-[14px] font-normal text-primary-500 flex gap-4">
							<div>{data?.rfq?.rfxPublished
								? `Issued on ${dayjs(data?.rfq?.rfxPublished)
									.tz(timezoneChecker(data?.rfq?.rfxPublished || "AWST"))
									.format("DD MMM YYYY")} `
								: data?.rfq?.supplierList?.name || "-"}</div>
							<div>{data?.rfq?.rfxType?.name}</div>
						</div>
					</div>
					{data?.rfq?.unreadMessages > 0 && <div className="relative flex justify-end">
						<UnreadMessage value={data?.rfq?.unreadMessages} onClick={navigateToComms} />
					</div>}
				</div>
				<div
					className="flex items-center justify-between space-x-0 border-b border-solid border-[#EAECF0]"
					style={{ height: "40px" }}
				>
					<div className="font-roboto text-[14px] font-normal text-primary-500">
						Client
					</div>
					<div className="font-roboto text-[14px] font-semibold text-primary-900">
						{data?.rfq?.client}
					</div>
				</div>
				<div
					className="flex items-center justify-between space-x-0 border-b border-solid border-[#EAECF0]"
					style={{ height: "40px" }}
				>
					<div className="font-roboto text-[14px] font-normal text-primary-500">
						RFx status
					</div>
					<RFQStatusComp
						isApproved={isRfxApproved(data?.rfq)}
						status={data?.rfq?.status}
						isRoundedFull={false}
					></RFQStatusComp>
				</div>

				<div
					className="flex items-center justify-between space-x-0 border-b border-solid border-[#EAECF0]"
					style={{ height: "40px" }}
				>
					<div className="font-roboto text-[14px] font-normal text-primary-500">
						Submission Deadline
					</div>
					<div>
						<SubmissionDeadline
							className="text-end font-roboto text-[14px] font-semibold text-primary-900"
							deadlineDateTime={submissionDeadlineDateTime}
							deadlineTimeZone={data?.rfq?.submissionTimezone}
							submissionStatus={data?.status}
						/>
					</div>
				</div>
				{invite_type !== GUEST_INVITE_TYPE.RFX_PRE_APPROVER && (
					<div className="flex h-[60px] items-start justify-between space-x-0 pt-3">
						<div className="font-roboto text-[14px] font-normal text-primary-500">
							Response
						</div>
						<RfxResponseComp
							response={data?.status}
							responseDate={data?.submittedAt}
							size={undefined}
						></RfxResponseComp>
					</div>
				)}
				{data?.status !== RFX_STATUS.DECLINE && (
					// @ts-ignore
					<Button
						btntype="secondary"
						width="100%"
						className="mt-5"
						data-testid="button-rfx-view"
						onClick={handleRfxCardClick}
					>
						<Flex align={"center"}>
							<p className="font-roboto font-semibold text-utility-blue-dark-100">
								{renderButtonRFQView(
									data?.status,
									data?.rfq?.status,
									invite_type,
									data?.rfq
								)}
							</p>
						</Flex>
					</Button>
				)}
			</div>
		</div>
	);
};

export default RfqCard;
